import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useCallback, useState } from 'react';
import type { BulkUpdateProjectsPayload, ProjectsSearchPayload } from '~/api/projectsV3';
import type { Project } from '~/common/types';
import { useBulkUpdateProjects } from '../../../queries';
import { setTableSelection } from '../../../store';

export type ProjectDates = {
  startDate?: string;
  endDate?: string;
  constructionStartDate?: string;
  constructionEndDate?: string;
};

export type UseBulkEditDatesParams = {
  organizationId: string | null;
  buildSearchPayloadForBulk: () => ProjectsSearchPayload;
  getProjectById?: (id: string) => Project | undefined;
};

export function useBulkEditDates({
  organizationId,
  buildSearchPayloadForBulk,
  getProjectById,
}: UseBulkEditDatesParams) {
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [constructionStartDate, setConstructionStartDate] = useState('');
  const [constructionEndDate, setConstructionEndDate] = useState('');

  const mutation = useBulkUpdateProjects();

  const open = useCallback(
    (selection: ServerSideSelectionState) => {
      setPendingSelection(selection);
      const isSingleProject = selection.mode === 'partial' && selection.selectedIds.length === 1 && getProjectById;
      if (isSingleProject) {
        const project = getProjectById(selection.selectedIds[0]);
        if (project) {
          setStartDate(project.startDate?.toISODate() ?? '');
          setEndDate(project.endDate?.toISODate() ?? '');
          setConstructionStartDate(project.constructionStartDate?.toISODate() ?? '');
          setConstructionEndDate(project.constructionEndDate?.toISODate() ?? '');
          return;
        }
      }
      setStartDate('');
      setEndDate('');
      setConstructionStartDate('');
      setConstructionEndDate('');
    },
    [getProjectById],
  );

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const onSubmit = useCallback(() => {
    if (!organizationId || !pendingSelection) return;
    const updates: BulkUpdateProjectsPayload = {};
    if (startDate) updates.startDate = startDate;
    if (endDate) updates.endDate = endDate;
    if (constructionStartDate) updates.constructionStartDate = constructionStartDate;
    if (constructionEndDate) updates.constructionEndDate = constructionEndDate;
    if (Object.keys(updates).length === 0) return;

    mutation.mutate(
      {
        organizationId,
        selection:
          pendingSelection.mode === 'all'
            ? { ...pendingSelection, search: buildSearchPayloadForBulk() }
            : pendingSelection,
        updates,
      },
      {
        onSuccess: () => {
          setPendingSelection(null);
          setTableSelection({ mode: 'partial', selectedIds: [] });
        },
      },
    );
  }, [
    organizationId,
    pendingSelection,
    startDate,
    endDate,
    constructionStartDate,
    constructionEndDate,
    buildSearchPayloadForBulk,
    mutation,
  ]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    constructionStartDate,
    setConstructionStartDate,
    constructionEndDate,
    setConstructionEndDate,
    onSubmit,
    isPending: mutation.isPending,
  };
}
